import { useQuery } from '@tanstack/react-query'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { fetchProjectForms, type DataIoForm } from '../api/forms'

/** Published-form picker grouped by form group. Only published forms qualify. */
export function FormPicker({
  projectId,
  value,
  onChange,
  publishedOnly = true,
}: {
  projectId: string
  value: string
  onChange: (form: DataIoForm) => void
  publishedOnly?: boolean
}) {
  const { data: forms = [], isLoading } = useQuery({
    queryKey: ['data-io-forms', projectId],
    queryFn: () => fetchProjectForms(projectId),
    enabled: !!projectId,
  })

  const eligible = publishedOnly ? forms.filter((f) => f.isPublished && f.currentVersionId) : forms

  // Group by group title for a tidy menu.
  const byGroup = new Map<string, DataIoForm[]>()
  for (const f of eligible) {
    const g = f.groupTitle || 'بدون گروه'
    if (!byGroup.has(g)) byGroup.set(g, [])
    byGroup.get(g)!.push(f)
  }

  return (
    <Select
      value={value}
      onValueChange={(id) => {
        const f = eligible.find((x) => x.id === id)
        if (f) onChange(f)
      }}
    >
      <SelectTrigger>
        <SelectValue placeholder={isLoading ? 'در حال بارگذاری…' : 'یک فرم را انتخاب کنید'} />
      </SelectTrigger>
      <SelectContent>
        {[...byGroup.entries()].map(([group, items]) => (
          <SelectGroup key={group}>
            <SelectLabel>{group}</SelectLabel>
            {items.map((f) => (
              <SelectItem key={f.id} value={f.id}>
                {f.title}
                <span className="text-xs text-muted-foreground ms-2">
                  {f.instanceType === 'repeatable' ? 'تکرارپذیر' : 'تک‌نمونه'}
                </span>
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
        {eligible.length === 0 && !isLoading && (
          <div className="px-3 py-2 text-sm text-muted-foreground">فرم منتشرشده‌ای یافت نشد</div>
        )}
      </SelectContent>
    </Select>
  )
}
